/**
 * Activity Monitoring Service
 * 
 * Tracks and logs all user activities throughout the application.
 * Provides comprehensive audit trail and activity analytics.
 * 
 * @module ActivityMonitoringService
 */

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  serverTimestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Activity action types
 */
export type ActivityAction =
  | 'login'
  | 'logout'
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  | 'export'
  | 'upload'
  | 'download'
  | 'approve'
  | 'reject'
  | 'submit'
  | 'navigate';

/**
 * Activity entity types
 */
export type ActivityEntity =
  | 'user'
  | 'store'
  | 'order'
  | 'note'
  | 'document'
  | 'report'
  | 'session'
  | 'page';

/**
 * Activity data interface
 */
export interface ActivityData {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  action: ActivityAction;
  entity: ActivityEntity;
  entityId?: string;
  entityName?: string;
  description: string;
  details?: Record<string, any>;
  timestamp: Date | Timestamp;
  ipAddress?: string;
  userAgent?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    city?: string;
    region?: string;
    country?: string;
  };
  metadata?: Record<string, any>;
}

/**
 * Activity filters for querying
 */
export interface ActivityFilters {
  userId?: string;
  userRole?: string;
  action?: ActivityAction | ActivityAction[];
  entity?: ActivityEntity | ActivityEntity[];
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
}

/**
 * Activity statistics
 */
export interface ActivityStatistics {
  totalActivities: number;
  activitiesByAction: Record<ActivityAction, number>;
  activitiesByEntity: Record<ActivityEntity, number>;
  activitiesByUser: Record<string, number>;
  mostActiveUsers: Array<{
    userId: string;
    userName: string;
    activityCount: number;
  }>;
  recentActivities: ActivityData[];
  activityTrend: Array<{
    date: string;
    count: number;
  }>;
}

/**
 * Activity Monitoring Service Class
 */
export class ActivityMonitoringService {
  private static readonly ACTIVITY_COLLECTION = 'activities';
  private static readonly USER_SESSIONS_COLLECTION = 'userSessions';

  /**
   * Log an activity
   * 
   * @param activityData - Activity data to log
   * @returns Promise resolving to activity ID
   */
  static async logActivity(activityData: Omit<ActivityData, 'id' | 'timestamp'>): Promise<string> {
    try {
      const activityRef = collection(db, this.ACTIVITY_COLLECTION);
      
      const activity = {
        ...activityData,
        timestamp: serverTimestamp(),
        ipAddress: activityData.ipAddress || await this.getClientIP(),
        userAgent: activityData.userAgent || navigator.userAgent,
      };

      const docRef = await addDoc(activityRef, activity);
      console.log('Activity logged:', docRef.id);
      
      return docRef.id;
    } catch (error: any) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging should never break the app
      return '';
    }
  }

  /**
   * Log user login
   */
  static async logLogin(userId: string, userName: string, userEmail: string, userRole: string): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'login',
      entity: 'session',
      description: `${userName} logged in`,
      details: {
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log user logout
   */
  static async logLogout(userId: string, userName: string, userEmail: string, userRole: string): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'logout',
      entity: 'session',
      description: `${userName} logged out`,
      details: {
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log page navigation
   */
  static async logNavigation(
    userId: string,
    userName: string,
    userEmail: string,
    userRole: string,
    pageName: string,
    pageUrl: string
  ): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'navigate',
      entity: 'page',
      entityName: pageName,
      description: `Navigated to ${pageName}`,
      details: {
        url: pageUrl,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log entity creation
   */
  static async logCreate(
    userId: string,
    userName: string,
    userEmail: string,
    userRole: string,
    entity: ActivityEntity,
    entityId: string,
    entityName: string,
    details?: Record<string, any>
  ): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'create',
      entity,
      entityId,
      entityName,
      description: `Created ${entity}: ${entityName}`,
      details
    });
  }

  /**
   * Log entity update
   */
  static async logUpdate(
    userId: string,
    userName: string,
    userEmail: string,
    userRole: string,
    entity: ActivityEntity,
    entityId: string,
    entityName: string,
    changes?: Record<string, any>
  ): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'update',
      entity,
      entityId,
      entityName,
      description: `Updated ${entity}: ${entityName}`,
      details: { changes }
    });
  }

  /**
   * Log entity deletion
   */
  static async logDelete(
    userId: string,
    userName: string,
    userEmail: string,
    userRole: string,
    entity: ActivityEntity,
    entityId: string,
    entityName: string,
    reason?: string
  ): Promise<void> {
    await this.logActivity({
      userId,
      userName,
      userEmail,
      userRole,
      action: 'delete',
      entity,
      entityId,
      entityName,
      description: `Deleted ${entity}: ${entityName}`,
      details: { reason }
    });
  }

  /**
   * Get activities with filters
   * 
   * @param filters - Activity filters
   * @param pageSize - Number of results per page
   * @returns Promise resolving to activities
   */
  static async getActivities(
    filters?: ActivityFilters,
    pageSize: number = 50
  ): Promise<{ activities: ActivityData[]; hasMore: boolean }> {
    try {
      const activityRef = collection(db, this.ACTIVITY_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters) {
        if (filters.userId) {
          constraints.push(where('userId', '==', filters.userId));
        }

        if (filters.userRole) {
          constraints.push(where('userRole', '==', filters.userRole));
        }

        if (filters.action) {
          if (Array.isArray(filters.action)) {
            constraints.push(where('action', 'in', filters.action));
          } else {
            constraints.push(where('action', '==', filters.action));
          }
        }

        if (filters.entity) {
          if (Array.isArray(filters.entity)) {
            constraints.push(where('entity', 'in', filters.entity));
          } else {
            constraints.push(where('entity', '==', filters.entity));
          }
        }

        if (filters.startDate) {
          constraints.push(where('timestamp', '>=', Timestamp.fromDate(filters.startDate)));
        }

        if (filters.endDate) {
          constraints.push(where('timestamp', '<=', Timestamp.fromDate(filters.endDate)));
        }
      }

      // Sort by timestamp descending (most recent first)
      constraints.push(orderBy('timestamp', 'desc'));
      
      // Add limit
      constraints.push(limit(pageSize + 1));

      const q = query(activityRef, ...constraints);
      const querySnapshot = await getDocs(q);

      const activities: ActivityData[] = [];
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      // Process results
      const docsToProcess = hasMore ? docs.slice(0, -1) : docs;

      for (const doc of docsToProcess) {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || data.timestamp
        } as ActivityData);
      }

      // Apply client-side filters
      let filteredActivities = activities;

      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredActivities = activities.filter(activity =>
          activity.description.toLowerCase().includes(searchLower) ||
          activity.userName.toLowerCase().includes(searchLower) ||
          activity.entityName?.toLowerCase().includes(searchLower)
        );
      }

      return {
        activities: filteredActivities,
        hasMore
      };
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      throw new Error(`Failed to fetch activities: ${error.message}`);
    }
  }

  /**
   * Get activity statistics
   * 
   * @param startDate - Start date for statistics
   * @param endDate - End date for statistics
   * @returns Promise resolving to activity statistics
   */
  static async getActivityStatistics(
    startDate?: Date,
    endDate?: Date
  ): Promise<ActivityStatistics> {
    try {
      const filters: ActivityFilters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const { activities } = await this.getActivities(filters, 1000);

      const stats: ActivityStatistics = {
        totalActivities: activities.length,
        activitiesByAction: {} as Record<ActivityAction, number>,
        activitiesByEntity: {} as Record<ActivityEntity, number>,
        activitiesByUser: {},
        mostActiveUsers: [],
        recentActivities: activities.slice(0, 10),
        activityTrend: []
      };

      // Count by action
      activities.forEach(activity => {
        stats.activitiesByAction[activity.action] = (stats.activitiesByAction[activity.action] || 0) + 1;
        stats.activitiesByEntity[activity.entity] = (stats.activitiesByEntity[activity.entity] || 0) + 1;
        stats.activitiesByUser[activity.userId] = (stats.activitiesByUser[activity.userId] || 0) + 1;
      });

      // Get most active users
      const userCounts = Object.entries(stats.activitiesByUser)
        .map(([userId, count]) => {
          const userActivity = activities.find(a => a.userId === userId);
          return {
            userId,
            userName: userActivity?.userName || 'Unknown',
            activityCount: count
          };
        })
        .sort((a, b) => b.activityCount - a.activityCount)
        .slice(0, 10);

      stats.mostActiveUsers = userCounts;

      // Calculate activity trend (last 7 days)
      const trendDays = 7;
      const today = new Date();
      const trendData: Record<string, number> = {};

      for (let i = 0; i < trendDays; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        trendData[dateStr] = 0;
      }

      activities.forEach(activity => {
        const activityDate = activity.timestamp instanceof Date 
          ? activity.timestamp 
          : new Date(activity.timestamp);
        const dateStr = activityDate.toISOString().split('T')[0];
        if (trendData[dateStr] !== undefined) {
          trendData[dateStr]++;
        }
      });

      stats.activityTrend = Object.entries(trendData)
        .map(([date, count]) => ({ date, count }))
        .reverse();

      return stats;
    } catch (error: any) {
      console.error('Error calculating activity statistics:', error);
      throw new Error(`Failed to calculate statistics: ${error.message}`);
    }
  }

  /**
   * Get client IP address (best effort)
   * 
   * @private
   */
  private static async getClientIP(): Promise<string> {
    try {
      // This would typically be done server-side or via a service
      // For client-side, we return a placeholder
      return 'client-ip';
    } catch (error) {
      return 'unknown';
    }
  }
}

